import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, Subject} from 'rxjs';
import {tap} from 'rxjs/operators';

import {WorkflowDefinitionControllerService} from '../api/api/workflowDefinitionController.service';
import {StateConfigService} from './state/state-config.service';

/**
 * Sichtbarkeitsbereich eines angehefteten Checks.
 * Spiegelt das Backend-Enum {@code PinScope} wider.
 */
export type PinScope = 'USER' | 'PROJECT' | 'ORGANISATION';

/** Pin-Zustand eines Checks im aktuellen Projektkontext (für das Anheften-Popup). */
export interface PinState {
  pinned: boolean;
  projectScoped: boolean;
  organisationScoped: boolean;
}

/** Angehefteter Check – Grundlage für die Seitenleisten-Shortcuts. */
export interface PinnedCheck {
  pinId: string;
  definitionId: string;
  name: string;
  projectId: number;
  scope: PinScope;
  status: string;
}

/**
 * Service für angeheftete Checks (Sidebar-Shortcuts).
 *
 * <p>HINWEIS: Die Endpunkte werden hier – analog zu {@code WorkflowService} (activate/deactivate) –
 * direkt per HttpClient angesprochen. Sobald der OpenAPI-Client neu generiert wurde, kann dieser
 * Service auf den generierten {@code WorkflowPinControllerService} umgestellt werden.
 */
@Injectable({providedIn: 'root'})
export class CheckPinService {
  private http = inject(HttpClient);
  private definitionApi = inject(WorkflowDefinitionControllerService);
  private stateConfig = inject(StateConfigService);

  /** Emittiert, wenn sich Pins geändert haben – die Sidebar lädt daraufhin neu. */
  private readonly pinsChangedSubject = new Subject<void>();
  readonly pinsChanged$ = this.pinsChangedSubject.asObservable();

  notifyPinsChanged(): void {
    this.pinsChangedSubject.next();
  }

  private get basePath(): string {
    return (this.definitionApi as any).basePath ?? '';
  }

  /** Pin-Zustand eines Checks im (aktuellen) Projektkontext abfragen. */
  getPinState(definitionId: string, projectId?: number): Observable<PinState> {
    const pid = projectId ?? this.stateConfig.getProjectId();
    return this.http.get<PinState>(`${this.basePath}/workflows/definitions/${definitionId}/pins`, {
      params: {projectId: String(pid)},
      withCredentials: true,
    });
  }

  /** Check anheften bzw. Scope setzen (Toggle-Semantik). */
  setPin(definitionId: string,
         body: { projectScoped: boolean; organisationScoped: boolean },
         projectId?: number): Observable<PinState> {
    const pid = projectId ?? this.stateConfig.getProjectId();
    return this.http.post<PinState>(`${this.basePath}/workflows/definitions/${definitionId}/pins`, body, {
      params: {projectId: String(pid)},
      withCredentials: true,
    }).pipe(tap(() => this.pinsChangedSubject.next()));
  }

  /** Check im aktuellen Projektkontext lösen (alle vom User verwalteten Pins). */
  unpin(definitionId: string, projectId?: number): Observable<PinState> {
    const pid = projectId ?? this.stateConfig.getProjectId();
    return this.http.delete<PinState>(`${this.basePath}/workflows/definitions/${definitionId}/pins`, {
      params: {projectId: String(pid)},
      withCredentials: true,
    }).pipe(tap(() => this.pinsChangedSubject.next()));
  }

  /** Alle für den aktuellen User sichtbaren Shortcuts (Seitenleiste). */
  listPins(projectId?: number): Observable<PinnedCheck[]> {
    const pid = projectId ?? this.stateConfig.getProjectId();
    return this.http.get<PinnedCheck[]>(`${this.basePath}/workflows/pins`, {
      params: {projectId: String(pid)},
      withCredentials: true,
    });
  }

  /** Einen konkreten Pin lösen (z.B. aus dem Sidebar-Kontextmenü). */
  deletePin(pinId: string): Observable<{ [key: string]: string }> {
    return this.http.delete<{ [key: string]: string }>(`${this.basePath}/workflows/pins/${pinId}`, {
      withCredentials: true,
    }).pipe(tap(() => this.pinsChangedSubject.next()));
  }
}



